import type { Placement } from '@floating-ui/react';
import { flip, offset as floatingUIOffset, hide, shift } from '@floating-ui/react';
import { ClickAwayListener, type ClickAwayListenerProps } from '@mui/base/ClickAwayListener';
import type { PopupMiddleware, PopupOwnProps } from '@mui/base/Unstable_Popup';
import { Unstable_Popup as BasePopup } from '@mui/base/Unstable_Popup';
import getAdaptedRemValue from '@unifyapps/style/helpers/getAdaptedRemValue';
import { clsx } from 'clsx';
import type { CSSProperties, Ref } from 'react';
import { forwardRef, useCallback, useMemo } from 'react';
import { slowCn } from '../../../lib/utils';
import { useContainerElementContext } from '../../../providers/ContainerElementContext';
import { usePopoverContainerContext } from '../../../providers/PopoverContainerContext';
import { useThemeOverrideContext } from '../../../providers/ThemeOverrideContext';
import { Box } from '../../Box';
import { type PopoverContextValue, usePopoverContext } from '../PopoverContext';
import type { PopoverContentProps } from './types';
import { getPopoverFallbackPlacementByPlacement } from './utils';

//floating-ui direction to fallback if it is overflowing from viewport
const getPopoverMiddleWare = ({
  offset,
  placement,
}: {
  offset: PopupOwnProps['offset'];
  placement: PopupOwnProps['placement'];
}) =>
  [
    flip({
      fallbackPlacements: getPopoverFallbackPlacementByPlacement(placement) as Placement[],
    }),
    // these two middlewares added by joy ui as we are overriding the default middleware
    //need to add these two middlewares to make it work as expected
    floatingUIOffset(offset),
    shift(),
    // Add hide middleware to detect when reference is hidden
    hide({
      strategy: 'referenceHidden',
    }),
  ] as PopupMiddleware[];

const getAdaptedRem = (value) => getAdaptedRemValue(`${value}rem`);

const getStyleWhenAdaptionDisabled = ({
  finalMinWidth,
  styleProp,
  useAnchorElWidth,
}: {
  styleProp: CSSProperties;
  finalMinWidth: number;
  useAnchorElWidth?: boolean;
}) => {
  if (useAnchorElWidth) {
    return {
      ...styleProp,
      minWidth: getAdaptedRem(finalMinWidth),
    };
  }
  return styleProp;
};

const getStyleWhenAdaptionEnabled = ({
  finalMinWidth,
  maxWidth,
}: {
  maxWidth?: number;
  finalMinWidth: number;
}) => {
  const style: React.ComponentProps<typeof Box>['style'] = {};
  if (maxWidth) style.maxWidth = getAdaptedRem(maxWidth);
  if (finalMinWidth) style.minWidth = getAdaptedRem(finalMinWidth);
  return style;
};

const PopoverChildrenContainerWrapper = forwardRef(function PopoverChildrenContainer(
  {
    className,
    style,
    children,
    ...rest
  }: {
    children: React.ReactNode;
    style: React.ComponentProps<typeof Box>['style'];
    className?: string;
  },
  ref: Ref<HTMLDivElement> | undefined,
) {
  return (
    <Box
      className={slowCn(
        `border-secondary bg-primary overflow-hidden rounded-lg is-platform:rounded-md border shadow-lg
        is-platform:shadow-popover-content`,
        className,
      )}
      ref={ref}
      style={style}
      {...rest}
    >
      {children}
    </Box>
  );
});

const popoverChildrenRenderer = ({
  children,
  disableClickAway,
  style,
  className,
  onClickAway,
  dataColorScheme,
  mouseEvent = 'onClick',
  triggerType,
}: {
  children: React.ReactNode;
  disableClickAway?: boolean;
  style: React.ComponentProps<typeof Box>['style'];
  className?: string;
  onClickAway: (event?: MouseEvent | TouchEvent) => void;
  dataColorScheme: Record<string, string>;
  mouseEvent?: ClickAwayListenerProps['mouseEvent'];
  triggerType?: PopoverContextValue['triggerType'];
}) => {
  if (disableClickAway) {
    return (
      <PopoverChildrenContainerWrapper className={className} style={style} {...dataColorScheme}>
        {children}
      </PopoverChildrenContainerWrapper>
    );
  }
  /**
   * Issue - onClickAway was not being fired properly on mobile devices when triggerType is longPress.
   *         Causing multiple popovers to remain open.
   * How to reproduce? - Long press while moving the finger on the screen, the clickaway is not fired.
   * RCA - MUI's logic is if click away happens while/after "touchmove" event, then don't call onClickAway.
   *       Ref: MUI - https://github.dev/mui/material-ui/blob/master/packages/mui-material/src/ClickAwayListener/index.ts
   *            Long Press handling on our side - packages/hooks/src/useLongPress/index.ts
   *
   * Solution - We can disable this behaviour by setting touchEvent to false.
   */
  return (
    <ClickAwayListener
      mouseEvent={mouseEvent}
      onClickAway={onClickAway}
      touchEvent={triggerType === 'longPress' ? false : undefined}
    >
      <PopoverChildrenContainerWrapper className={className} style={style} {...dataColorScheme}>
        {children}
      </PopoverChildrenContainerWrapper>
    </ClickAwayListener>
  );
};

function Content(props: React.PropsWithChildren<PopoverContentProps>) {
  const {
    popupClassName,
    children,
    style: styleProp = {},
    maxWidth,
    minWidth = 16,
    placement = 'bottom-start',
    className,
    offset = 6,
    anchorEl: anchorElProp,
    useAnchorElWidth,
    disableClickAway,
    useAnchorAsContainer,
    ignoreBoundary,
    disableStyleAdaptation,
    transform,
    mouseEvent,
    disableOnMiddleWareData = false,
    onClickAway,
    shouldIgnoreClickAway,
    containerEl,
    strategy,
    ...rest
  } = props;
  const { anchorEl, onClose, open, onMouseMove, longPressJustOpenedRef, triggerType } =
    usePopoverContext({
      showError: false,
    });
  const finalAnchorEl = anchorElProp ?? anchorEl;
  // Container precedence: prop > `PopoverContainerContext` (narrow, e.g. set
  // by BottomSheet) > global `ContainerElementContext`. Narrow scope exists so
  // BottomSheet can re-home popovers into its portal subtree without dragging
  // Modal/Drawer along.
  const popoverContainerEl = usePopoverContainerContext();
  const globalContainerEl = useContainerElementContext();
  const finalContainerEl = containerEl || popoverContainerEl || globalContainerEl;

  // we are using offsetWidth to get the width of the anchor element including the scrollbar
  const anchorElWidth = useMemo(() => {
    if (!useAnchorElWidth || !finalAnchorEl?.offsetWidth) return undefined;
    return finalAnchorEl.offsetWidth / 16;
  }, [finalAnchorEl, useAnchorElWidth]);

  const style = useMemo(() => {
    // if anchorElWidth is defined, we are using it to set the width of the popover
    if (anchorElWidth) {
      return { ...styleProp, width: getAdaptedRem(anchorElWidth) };
    }

    if (disableStyleAdaptation) {
      return getStyleWhenAdaptionDisabled({ styleProp, finalMinWidth: minWidth });
    }

    return getStyleWhenAdaptionEnabled({ maxWidth, finalMinWidth: minWidth });
  }, [disableStyleAdaptation, maxWidth, minWidth, styleProp, anchorElWidth]);

  // used to override theme for popovers originating from the interface canvas
  const { themeOverrideClassName, scheme } = useThemeOverrideContext();
  const dataColorScheme: Record<string, string> = scheme ? { 'data-color-scheme': scheme } : {};
  const classNameWithThemeClasses = `${className ?? ''} ${themeOverrideClassName}`;

  const handleClickAway = useCallback(
    (event) => {
      const ignoreClickAway = shouldIgnoreClickAway?.(event);
      if (ignoreClickAway) return;

      // ignore synthetic clicks fired immediately after this popover was opened via long press
      // when a long press opens this popover, it fires a synthetic click to close other popovers
      // this check ensures we don't close ourselves from that synthetic click
      if (longPressJustOpenedRef?.current) {
        return;
      }

      onClose();
      onClickAway?.(event);
    },
    [onClose, onClickAway, shouldIgnoreClickAway, longPressJustOpenedRef],
  );

  return (
    <BasePopup
      anchor={finalAnchorEl}
      className={clsx('z-popup ua-popover', popupClassName)}
      container={finalContainerEl}
      disablePortal={useAnchorAsContainer}
      id="popover-content"
      middleware={
        ignoreBoundary
          ? undefined
          : getPopoverMiddleWare({
              offset,
              placement,
            })
      }
      offset={offset}
      // @ts-expect-error -- we have patched the popup of mui base and added this prop
      onMiddlewareData={(data) => {
        if (disableOnMiddleWareData) {
          return;
        }

        // Hide the popover when reference is hidden
        if (data.hide?.referenceHidden) {
          if (open) {
            onClose();
          }
        }
      }}
      onMouseEnter={onMouseMove}
      onMouseLeave={onMouseMove}
      open={open}
      placement={placement}
      strategy={strategy ?? (useAnchorAsContainer ? 'fixed' : undefined)}
      // withTransition // todo: check in migration
      transform={transform}
      {...rest}
    >
      {popoverChildrenRenderer({
        children,
        disableClickAway,
        style,
        className: classNameWithThemeClasses,
        onClickAway: handleClickAway,
        dataColorScheme,
        mouseEvent,
        triggerType,
      })}
    </BasePopup>
  );
}

function PopoverContent(props: React.PropsWithChildren<PopoverContentProps>) {
  const { open } = usePopoverContext();

  if (!open) {
    return null;
  }

  return <Content {...props} />;
}

export type { PopoverContentProps };
export default PopoverContent;
